import type { UserInformation } from '~/types/auth'
import { useQRStaticStore } from '~/stores/qrStatic'

interface AuthState {
  token: string | null
  user: UserInformation | null
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    token: null,
    user: null,
  }),

  getters: {
    isAuthenticated: state => !!state.token,
    getUser: state => state.user,
    getToken: state => state.token,
  },

  actions: {
    setUser(user: UserInformation) {
      this.user = user
    },
    setAuth(token: string, user: UserInformation) {
      this.token = token
      this.user = user

      // Persist to cookies FIRST to ensure they're set before API calls
      useCookiee.set('auth_token', token, {
        expires: 30,
        sameSite: 'lax',
        encrypt: false,
      })

      useCookiee.set('auth_user', user, {
        expires: 30,
        sameSite: 'lax',
        encrypt: false,
      })

      const apiV1 = useApi('v1')
      const apiV2 = useApi('v2')
      apiV1.setToken(token)
      apiV2.setToken(token)
    },

    updateUser(userData: Partial<UserInformation>) {
      if (this.user) {
        const data = { ...this.user, ...userData }

        // Update user cookie
        useCookiee.set('auth_user', data, {
          expires: 30,
          sameSite: 'lax',
          encrypt: false,
        })
      }
    },

    logout() {
      // api.post('billing/auth/logout')
      this.token = null
      this.user = null

      // Clear token from API clients (both v1 and v2)
      const apiV1 = useApi('v1')
      const apiV2 = useApi('v2')
      apiV1.setToken(null)
      apiV2.setToken(null)

      // Clear QR static store
      const qrStaticStore = useQRStaticStore()
      qrStaticStore.reset()

      if (import.meta.client) {
        const nuxtApp = useNuxtApp()
        const queryClient = nuxtApp.$queryClient as any
        if (queryClient) {
          queryClient.clear() // Clear all cached queries
        }
        localStorage.removeItem('penagihan-clients')
      }

      // Remove cookies
      useCookiee.remove('auth_token')
      useCookiee.remove('auth_user')
    },

    /**
     * Initialize authentication state from cookies
     */
    initFromStorage() {
      try {
        const token = useCookiee.get<string>('auth_token', { encrypt: false })
        const user = useCookiee.get<UserInformation>('auth_user', { encrypt: false })
        if (token && user) {
          this.token = token
          this.user = user

          // Set token in API clients (both v1 and v2)
          const apiV1 = useApi('v1')
          const apiV2 = useApi('v2')
          apiV1.setToken(token)
          apiV2.setToken(token)

          // Fetch QR list to determine menu visibility
          const qrStaticStore = useQRStaticStore()
          qrStaticStore.fetchQRList()
        }
      }
      catch (err) {
        console.error('Error initializing auth from cookies:', err)
        // If cookies are corrupted, clear them
        this.logout()
      }
    },
  },
})
